<template>
  <fts-client-modal
    title="Send form"
    append-content-class="share-form-modal__body"
    class="fts-share-form-modal"
    @closed="reset"
    ref="ftsShareFormModal"
  >
    <!-- header:icon -->
    <font-awesome-icon slot="icon" :icon="['far', 'share-from-square']" fixed-width />
    <!-- body -->
    <template #body>
      <!-- tab -->
      <div class="client-modal-tab">
        <div class="client-modal-tab-container">
          <div class="client-modal-tab__label">
            Send via
          </div>
          <div
            v-for="x in sections"
            :key="x.id"
            class="client-modal-tab__action"
            :class="{'client-modal-tab__action--active': x.id === section}"
            @click="changeTab(x.id)"
          >
            {{x.name}}
          </div>
        </div>
      </div>
      <!-- content -->
      <div class="comfort-form-design">
        <!-- email -->
        <div class="fts-form-group" v-if="section === 'email'" :key="section">
          <fts-form-label icon="fts-icon-envelope" placement="top">To</fts-form-label>
          <div
            class="fts-form-control textarea invite-box fts-scrollbar fts-scrollbar-overlay" 
            :class="{focus: inputFocused}"
            @click="focusEmailInput"
          >
            <div class="invite-placeholder" v-if="invitePlaceholderVisible">name@example.com</div>
            <div class="invite-box-field">
              <div 
                class="invite-email" 
                :class="{error: !x.valid}"
                v-for="x in inviteEmails" 
                :key="x.id"
              >
                <div>{{x.email}}</div>
                <font-awesome-icon :icon="['fas', 'times']" class="remove-email" @click="removeEmail(x)" />
              </div>
              <input 
                type="text"
                v-model="inviteInput" 
                @focus="inputFocused=true" 
                @blur="inputFocused=false;insertEmail()" 
                @keyup="inviteInputKeyup"
                @keydown.delete="inputDelete"
                @keydown.space.prevent
                @keydown.188.prevent
                @paste.prevent="inviteInputOnPaste"
                autocomplete="inviteInput"
                ref="inviteInput">
            </div>
          </div>
          <div class="fts-form-error" v-if="emailError">
            Invalid email format.
          </div>
        </div>
        <!-- link -->
        <template v-else-if="section === 'link'">
          <div class="fts-form-group">
            <fts-form-label icon="fts-icon-link" placement="top">Link</fts-form-label>
            <div class="share-link-container">
              <div class="share-link-wrapper">
                <fts-spinner show :size="20" v-if="invitationLinkLoading" />
                <span
                  title="Click to copy link"
                  v-clipboard:copy="invitationURL"
                  v-clipboard:success="clipboardOnCopy"
                  v-clipboard:error="clipboardOnError"
                  v-else-if="invitationURL"
                >
                  {{invitationURL}}
                </span>
              </div>
              <div class="share-link-hint" v-if="linkCopied">
                Copy to clipboard.
              </div>
            </div>
          </div>

          <div class="fts-form-group">
            <fts-form-label placement="top">
              Share Options
            </fts-form-label>
            <fts-checkbox v-model="queryComment">
              Show comment
            </fts-checkbox>
          </div>
        </template>
      </div>
      <!-- feedback indicate -->
      <fts-feedback-indicate modal ref="ftsFeedbackIndicate" />
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <fts-btn
        text
        :disabled="loading"
        @click="close"
      >
        <font-awesome-icon :icon="['fas', 'times']" /> Close
      </fts-btn>

      <fts-btn
        color="primary"
        :loading="loading"
        :disabled="disabledSend"
        @click.native.prevent="submit"
        v-if="section === 'email'"
      >
        <font-awesome-icon :icon="['fas', 'check']" /> Share
      </fts-btn>

      <fts-btn
        color="primary"
        :disabled="!invitationURL"
        v-clipboard:copy="invitationURL"
        v-clipboard:success="clipboardOnCopy"
        v-clipboard:error="clipboardOnError"
        v-else-if="section === 'link'"
      >
        <font-awesome-icon :icon="['far', 'clone']" /> Copy
      </fts-btn>
    </template>
  </fts-client-modal>
</template>

<script>
import Vue from 'vue';
import VueClipboard from 'vue-clipboard2';
Vue.use(VueClipboard);

import commons from "../commons/commons";
import { emailValidator } from "../commons/validator";
import { authorizationStore } from '../store/authorizationStore';
import { classificationStore } from '../store/classificationStore';

import FTSClientModal from "./FTSClientModal";
import FTSBtn from "./FTSBtn";
import FTSCheckbox from './FTSCheckbox';
import FTSSpinner from "./FTSSpinner";
import FTSFeedbackIndicate from "./FTSFeedbackIndicate";
import FTSFormLabel from "./FTSFormLabel";

let inviteInputCache = null;

export default {
  components: {
    "fts-client-modal": FTSClientModal,
    "fts-btn": FTSBtn,
    "fts-checkbox": FTSCheckbox,
    "fts-spinner": FTSSpinner,
    "fts-feedback-indicate": FTSFeedbackIndicate,
    "fts-form-label": FTSFormLabel,
  },
  data: () => ({
    sections: [
      { id: 'email', name: 'Email' },
      { id: 'link', name: 'Link' },
    ],
    section: 'email',
    formData: null,
    loading: false,
    error: false,
    inputFocused: false,
    inviteInput: null,
    inviteEmails: [],
    invitationLink: null,
    invitationLinkLoading: false,
    linkCopied: false,
    linkCopyTimeout: null,
    feedbackIndicateOption: {},
    query: {}
  }),
  methods: {
    show(item) {
      this.formData = _.cloneDeep(item);
      this.$refs.ftsShareFormModal.show();
      this.$nextTick(() => {
        this.focusEmailInput();
      });
    },
    close() {
      this.$refs.ftsShareFormModal.close();
    },
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    changeTab(tab) {
      this.section = tab;
      if (this.section === 'email') {
        this.$nextTick(() => {
          this.focusEmailInput();
        });
      } else if (this.section === 'link') {
        this.getInvitationLink();
      }
    },
    async getInvitationLink() {
      this.invitationLinkLoading = true;
      try {
        const res = await classificationStore.dispatch('shareForm', {
          ids: this.formData.id? [this.formData.id] : null,
          activityId: this.formData.activityId,
          moduleId: this.formData.moduleId,
          share: true,
        });
        this.invitationLink = res.data;
      } catch(err) {
        console.error(err);
      } finally {
        this.invitationLinkLoading = false;
      }
    },
    focusEmailInput() {
      this.$refs.inviteInput.focus();
    },
    inviteInputKeyup(evt) {
      if ([13, 32, 188].some(x => x === evt.keyCode)) 
        this.insertEmail()
    },
    inviteInputOnPaste(evt) {
      try {
        let emails = evt.clipboardData.getData('text/plain');
        emails = emails.replaceAll(/\s/g,'').split(",");
        emails.forEach(x => this.insertEmail(x));
      } catch(err) {}
    },
    insertEmail(data) {
      const email = this.inviteInput || data 
      if (email && email.length && !this.inviteEmails.some(x => x.email === email)) {
        this.inviteEmails.push({
          id: commons.nanoID(),
          email: email,
          valid: emailValidator(email),
        })
        this.inviteInput = null
      }
    },  
    removeEmail(data) {     
      const index = data ? this.inviteEmails.indexOf(data) : -1
      this.inviteEmails.splice(index, 1);
    },
    inputDelete() {
      inviteInputCache = this.inviteInput
      if (!this.inviteInput && !inviteInputCache && this.inviteEmails && this.inviteEmails.length) 
        this.removeEmail()
    },
    clipboardOnCopy() {
      const resetTimeout = () => {
        clearTimeout(this.linkCopyTimeout);
        this.linkCopyTimeout = null;
      }

      this.linkCopyTimeout && resetTimeout();
      this.linkCopied = true;
      this.linkCopyTimeout = setTimeout(() => {
        this.linkCopied = false;
        resetTimeout();
      }, 2000);
    },
    clipboardOnError() {
      console.error("ClipboardOnError");
    },
    async submit() {
      this.loading = true;
      try {

        const res = await classificationStore.dispatch('shareForm', {
          ids: this.formData.id? [this.formData.id] : null,
          activityId: this.formData.activityId,
          moduleId: this.formData.moduleId,
          emails: [...this.inviteEmails.map(x => x.email)],
          share: true,
        });
        this.$refs.ftsFeedbackIndicate.show({
          type: 'success',
          desc: res.data && res.data.message? res.data.message : "Form successfully share.",
          handler: () => {
            this.close()
          },
        });

      } catch(err) {
        this.$refs.ftsFeedbackIndicate.show({
          type: 'error',
          desc: 'Form failed to share, please try again later.',
          response: err.response,
        });
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    invitePlaceholderVisible() {
      return !this.inviteInput && !this.inviteEmails.length
    },
    emailError() {
      return this.inviteEmails.filter(x => !x.valid ).length && authorizationStore.state.userProfile.userName != 'admin'
    },
    disabledSend() {
      return !this.inviteInput && (this.inviteEmails.length === 0 || this.emailError) ? true : false
    },
    invitationURL() {
      if (!this.invitationLink) {
        return null;
      }

      if (Object.keys(this.query).length > 0) {
        const separator = this.invitationLink.includes('?') ? '&' : '?';

        return `${this.invitationLink}${separator}${Object.keys(this.query)
          .map(key => `${key}=${this.query[key]}`)
          .join('&')}`;
      }

      return this.invitationLink;
    },
    queryComment: {
      get() {
        return !(this.query && this.query.comment);
      },
      set(val) {
        val ? this.$delete(this.query, 'comment') : this.$set(this.query, 'comment', '0');
      }
    }
  },
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-share-form-modal {
  .share-form-modal__body {
    padding: 0 1rem 1rem;
  }
  /* to-do (move) */
  .client-modal-tab {
    position: sticky;
    top: 0;
    margin-left: -1rem;
    margin-right: -1rem;
    margin-bottom: 1rem;
    padding: 1rem;
    background-color: var(--fts-card-background);
    z-index: 2;
    .client-modal-tab-container {
      @include flex-gap($display: flex, $gapTop: .25rem, $gapLeft: 1.25rem);
      align-items: center;
      user-select: none;
      @mixin client-modal-tab-wrapper {
        padding-bottom: 0.05rem;
        border-bottom: 2px solid transparent;
        font-weight: 500;
      }
      .client-modal-tab__label {
        @include client-modal-tab-wrapper;
        color: var(--fts-color);
      }
      .client-modal-tab__action {
        @include client-modal-tab-wrapper;
        color: var(--fts-gray-400);
        transition: all 0.3s ease-in-out;
        cursor: pointer;
        &.client-modal-tab__action--active {
          @include theme() {
            border-color: rgba(theme-get('fts-primary'), 0.75);
          }
          color: var(--fts-primary);
        }
      }
    }
  }
  /* to-do */
  .invite-box {
    min-height: 88px !important;
    position: relative;
    &.focus {
      border-color: var(--fts-primary);
      outline: none;
      box-shadow: $input-box-shadow;
      -webkit-box-shadow: $input-box-shadow;
    }
    .invite-box-field {
      @include flex-gap($gap: 0.375rem);
      > input {
        flex: 1;
        padding: .375rem 0;
        border: 0;
        font-size: 1rem;
        background: inherit;
        color: inherit;
      }
    }
    .invite-placeholder {
      position: absolute;
      padding: .375rem 0;
      color: var(--fts-form-control-placeholder);
      line-height: 1.15;
    }
    .invite-email {
      @include theme() {
        background: rgba($color: theme-get('fts-primary'), $alpha: 0.15);
      }
      display: flex;
      align-items: center;
      padding: .375rem .575rem;
      border-radius: .25rem;
      line-height: 1.15;
      &.error {
        @include theme() {
          background: rgba($color: theme-get('fts-danger'), $alpha: 0.1);
        }
      }
      .remove-email {
        margin-left: .575rem;
        font-size: .75rem;
        color: var(--fts-gray-400);
        &:hover {
          cursor: pointer;
        }
      }
    }
  }
  .invite-error {
    display: flex;
    align-items: center;
    margin-top: 1rem;
    margin-left: .135rem;
    font-size: .875rem;
    color: var(--fts-danger);
    .error-icon {
      margin-right: .575rem;
    }
  }
  /* share link */
  .share-link-container {
    position: relative;
    .share-link-wrapper {
      padding: .75rem;
      border-radius: .375rem;
      background-color: var(--fts-gray-100);
      span {
        word-break: break-all;
        font-size: .875rem;
        line-height: 1.25rem;
        cursor: pointer;
      }
    }
    .share-link-hint {
      position: absolute;
      right: .5rem;
      bottom: .5rem;
      display: inline-flex;
      padding: .5rem;
      border-radius: .375rem;
      background-color: var(--fts-popover-background);
      color: #fff;
      font-size: .75rem;
    }
  }
}
</style>